'use strict';

import * as Sentry from '@sentry/browser';

/**
 * @param {String} dsn
 * @param {('dev'|'prod'|'staging')} environment
 * @param {Boolean} debug
 * @param {(Array|string)} thirdPartyFilterKeys
 * @param {number} replaysSessionSampleRate
 * @param {number} replaysOnErrorSampleRate
 * @param {Object} replayOptions
 */
window.initSentry = function (
    dsn,
    environment              = 'dev',
    debug                    = false,
    thirdPartyFilterKeys     = null,
    replaysSessionSampleRate = 0.0,
    replaysOnErrorSampleRate = 1.0,
    replayOptions            = {
        maskAllText:   false,
        blockAllMedia: false,

        networkDetailAllowUrls: [
            /^.+/,
        ],

        networkDetailDenyUrls: [
            /^https?:\/\/.*\.google\.com/,
            /^https?:\/\/.*\.doubleclick\.net/,
            /^https?:\/\/.*\.googlesyndication\.com/,
            /^https?:\/\/matomo\.fify\.services/,
        ],
    },
) {
    const enabled = !!dsn;

    const integrations = [
        Sentry.replayIntegration(replayOptions),
    ];

    if (thirdPartyFilterKeys) {
        const filterKeys = typeof thirdPartyFilterKeys !== 'object' ? thirdPartyFilterKeys : [thirdPartyFilterKeys];

        integrations.push(
            Sentry.thirdPartyErrorFilterIntegration({
                filterKeys: filterKeys,
                behaviour:  'drop-error-if-exclusively-contains-third-party-frames',
            })
        );
    }

    Sentry.init({
        enabled:                  enabled,
        debug:                    debug,
        dsn:                      dsn,
        environment:              environment,
        integrations:             integrations,
        replaysSessionSampleRate: replaysSessionSampleRate,
        replaysOnErrorSampleRate: replaysOnErrorSampleRate,

        ignoreErrors: [
            // mostly on deploy
            /^ChunkLoadError: Loading chunk/,
            /^ChunkLoadError: Loading CSS chunk/,

            // uncaught failed/aborted fetch in multiple browsers and failed requests
            /^TypeError: Failed to fetch/,
            /^TypeError: Load failed/,
            /^TypeError: NetworkError when attempting to fetch resource/,
            /^AbortError: The user aborted a request/,
            /^AbortError: The operation was aborted/,
            /^AbortError: Fetch is aborted/,
            /^AbortError: cancelled/,
            /^AbortError: Cancelled/,
            /^TypeError: Content-Length header of network response exceeds response Body./,

            // unhandled promises
            /^UnhandledRejection: Non-Error promise rejection captured with value:/,

            // resize observer
            /^ResizeObserver loop limit exceeded/,
            /^ResizeObserver loop completed with undelivered notifications/,

            // View transition errors (mostly mobile)
            /^InvalidStateError: Skipping view transition/,
            /^InvalidStateError: View transition was skipped/,
            /^InvalidStateError: One of the transitioned elements has become hidden/,
            /^AbortError: Old view transition aborted by new view transition/,
            /^TimeoutError: View transition update callback timed out/,
            /^UnhandledRejection: Object captured as promise rejection with keys: isFromCancelledTransition/,

            // Safari old doesn't support intersection observer
            /^ReferenceError: Can't find variable: IntersectionObserver/,

            // Safari, probably an extension problem
            /^Error: Invalid call to runtime.sendMessage/,

            // probably in-app browser of some Android apps
            /^TypeError: Cannot read properties of undefined \(reading 'domInteractive'\)/,

            // hard to reproduce problem, hard to fix, see https://github.com/symfony/ux/issues/2623
            /^Error: Tom Select already initialized on this element/,
            /^TypeError: undefined is not an object \(evaluating 'this.tomSelect.destroy'\)/,
            /^TypeError: Cannot read properties of undefined \(reading 'destroy'\)/,

            // probably sandboxed bots and/or old browsers, also Sentry JS errors
            /^SecurityError: Failed to read a named property/,
            /^TypeError: Cannot read properties of null \(reading 'touches'\)/,
            /^Error: db/,
            /^Error: missing id/,
            /^Error: Method not found/,

            // Alpine (if something failed before?)
            /^RangeError: Maximum call stack size exceeded/,

            // Turbo
            /^TypeError: undefined is not an object \(evaluating 'this.delegate.connect'\)/,
            /^TypeError: undefined is not an object \(evaluating 'this.delegate.disconnect'\)/,
            /^Error: The response \(200\) did not contain the expected <turbo-frame/,

            // Live Component requests failing
            /^Error: Component not found for element/,
        ],
    });
};
